<script lang="ts">
import { useStore } from "@/utils/vuex-utils"
import { defineComponent, computed } from "vue"

export default defineComponent({
    name: "ToastMessage",
    setup() {
        const store = useStore()
        const messages = computed(() => store.getters["toastMessageModule/messages"])
        const dismiss = (id: string) => store.dispatch("toastMessageModule/clearMessage", id)
        const clearAll = () => store.dispatch("toastMessageModule/clearAllMessages")

        const getHeaderBg = (color: string) => {
            return `color-mix(in srgb, ${color} 15%, transparent)`
        }

        return { messages, dismiss, clearAll, getHeaderBg }
    },
})
</script>

<template>
    <div v-if="messages.length > 0" class="toast-wrapper">
        <div class="toast-container">
            <div v-for="msg in messages" :key="msg.id" class="toast-msg">
                <div
                    class="toast-msg__header"
                    :style="`
                        border-color: ${msg.color || 'var(--theme-tertiary)'};
                        background-color: ${getHeaderBg(msg.color || 'var(--theme-tertiary)')};
                    `"
                >
                    <strong v-if="msg.title" class="toast-msg__title">{{
                        msg.isTranslation ? $t(msg.title) : msg.title
                    }}</strong>
                    <button class="toast-msg__close" @click="dismiss(msg.id)">&times;</button>
                </div>
                <div class="toast-msg__body">
                    {{ msg.isTranslation ? $t(msg.message) : msg.message }}
                    <a
                        v-if="msg.link"
                        :href="msg.link"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="toast-msg__link"
                        >{{ msg.linkText || msg.link }}</a
                    >
                    <p v-if="msg.description" class="toast-msg__description">
                        {{ msg.description }}
                    </p>
                </div>
            </div>
        </div>
        <button v-if="messages.length > 1" class="toast-clear-all" @click="clearAll">
            Clear All
        </button>
    </div>
</template>

<style scoped>
.toast-wrapper {
    position: fixed;
    z-index: 999999999;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    pointer-events: all;
}

.toast-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 50vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0.25rem;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
}

.toast-container::-webkit-scrollbar {
    width: 4px;
}

.toast-container::-webkit-scrollbar-track {
    background: transparent;
}

.toast-container::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
}

.toast-container::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
}

.toast-clear-all {
    background: var(--toast-bg);
    border: 1px solid var(--toast-border);
    color: var(--toast-text);
    border-radius: 0.5rem;
    padding: 0.35rem 0.75rem;
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    align-self: flex-end;
    margin-top: 0.25rem;
    margin-right: 0.25rem;
    transition: all 0.2s ease;
    opacity: 0.9;
}

.toast-clear-all:hover {
    background: var(--toast-header-bg);
    border-color: var(--theme-tertiary);
    opacity: 1;
}

.toast-msg {
    background-color: var(--toast-bg);
    border: 1px solid var(--toast-border);
    border-radius: 0.75rem;
    overflow: hidden;
    min-width: 380px;
    max-width: 380px;
    flex-shrink: 0;
    pointer-events: all;
    animation: toastSlideIn 0.25s ease;
}

.toast-msg__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.45rem 0.75rem;
    min-height: 2.1rem;
    border: 2px solid;
    border-bottom-width: 1px;
    margin: -1px -1px 0 -1px;
    border-radius: 0.75rem 0.75rem 0 0;
}

.toast-msg__title {
    color: var(--toast-text, #e8e9ec);
    font-size: 0.875rem;
    font-weight: 500;
}

.toast-msg__close {
    background: none;
    border: none;
    color: var(--toast-text, #e8e9ec);
    opacity: 0.5;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    font-size: 1.25rem;
    margin-left: auto;
}

.toast-msg__close:hover {
    opacity: 1;
}

.toast-msg__body {
    padding: 0.6rem 0.75rem;
    color: var(--toast-text, #e8e9ec);
    font-size: 0.875rem;
    line-height: 1.4;
}

.toast-msg__link {
    color: var(--theme-tertiary);
    white-space: nowrap;
}

.toast-msg__description {
    margin-top: 0.5rem;
    margin-bottom: 0;
    opacity: 0.8;
}

@keyframes toastSlideIn {
    from {
        opacity: 0;
        transform: translateX(20px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@media screen and (min-width: 768px) {
    .toast-wrapper {
        top: 1rem;
        right: 1rem;
        align-items: flex-end;
    }
}

@media screen and (max-width: 767px) {
    .toast-wrapper {
        top: 3.5rem;
        left: 1rem;
        right: 1rem;
        align-items: stretch;
    }

    .toast-container {
        max-height: 30vh;
    }

    .toast-msg {
        min-width: unset;
        max-width: unset;
        width: 100%;
    }
}
</style>
